/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AuthenticationTypeV3 } from "@tivocorp/data-domain-lib";
import type {
  DomainToken,
  ProvisionDevicesResponseV2
} from "@tivocorp/data-domain-lib";

import { DeviceModel } from "../../../model/device";
import { TokenManager } from "../../../model/auth";
import { Log } from "../../../model/logging";
import { STORAGE_KEY_ENABLE_IP_AUTOLOGIN } from "../../../model/storage";
import type { AuthTypeKind, IpAutoLoginStatus } from "../signIn.types";
import {
  AUTH_TYPE_KIND_INTERACTIVE,
  AUTH_TYPE_KIND_SILENT,
  AUTH_TYPE_KIND_UNSUPPORTED,
  IP_AUTO_LOGIN_STATUS_COMPLETED,
  IP_AUTO_LOGIN_STATUS_ERROR,
  IP_AUTO_LOGIN_STATUS_IDLE
} from "../signIn.types";
import { TOKEN_EXPIRY_SKEW_MS } from "../signIn.constants";

const TAG: string = "signIn/authHelpers";

/**
 * Feature flag for the silent IP auto-login flow, read from localStorage.
 *
 * The feature is **opt-in**: the attempt only runs when the key is explicitly
 * set to "true". Any other value — and an absent key — leaves it disabled, so
 * the auth walk falls through to the next authenticationType in displayRank
 * order.
 *
 *   localStorage.setItem("enableIpAutologin", "true")   → enabled
 *   localStorage.removeItem("enableIpAutologin")        → disabled
 */
export function isIpAutoLoginFeatureEnabled(): boolean {
  let flag: string | null = null;
  try {
    flag = localStorage.getItem(STORAGE_KEY_ENABLE_IP_AUTOLOGIN);
  } catch (error: unknown) {
    Log.w(
      TAG,
      "isIpAutoLoginFeatureEnabled() Unable to access localStorage for flag",
      error
    );
    return false;
  }
  return flag !== null && flag.toLowerCase() === "true";
}

/**
 * Classifies an authentication type string as silent (IP auto-login),
 * interactive (user-facing tabs), or unsupported.
 */
export function classifyAuthType(authType: string): AuthTypeKind {
  if (authType === AuthenticationTypeV3.TivoIpAutoLoginIdp) {
    return AUTH_TYPE_KIND_SILENT;
  }
  if (
    authType === AuthenticationTypeV3.TivoNativeIdp ||
    authType === AuthenticationTypeV3.DbsLicensePlateBinding
  ) {
    return AUTH_TYPE_KIND_INTERACTIVE;
  }
  return AUTH_TYPE_KIND_UNSUPPORTED;
}

/**
 * True while a silent IP auto-login flow is still running — every
 * ipAutoLoginStatus other than the three resting states is a step in
 * progress.
 */
export function isIpAutoLoginInFlight(status: IpAutoLoginStatus): boolean {
  return (
    status !== IP_AUTO_LOGIN_STATUS_IDLE &&
    status !== IP_AUTO_LOGIN_STATUS_COMPLETED &&
    status !== IP_AUTO_LOGIN_STATUS_ERROR
  );
}

/**
 * Returns true when the supplied domain token is missing or expired
 * (within TOKEN_EXPIRY_SKEW_MS of expiry).
 */
export function isDomainTokenExpired(token: DomainToken | null): boolean {
  if (token === null) {
    return true;
  }
  const expirationMs: number = token.expiration * 1000;
  return expirationMs - Date.now() <= TOKEN_EXPIRY_SKEW_MS;
}

/**
 * Stores bodyId / partnerId / partnerCustomerId from a provisioning response.
 * A response without a bodyId is not an error — there is simply nothing to
 * persist, and the sequence continues.
 *
 * Provisioning is also where a first-ever sign-in learns its bodyId, which is
 * the key persisted tokens are stored under — and the domain token was adopted
 * several steps earlier, under whatever bodyId the device had then (none, on a
 * first sign-in). The tokens are moved onto the new key before returning, so
 * the next boot restores the session instead of showing the sign-in screen.
 */
export async function persistProvisionData(
  response: ProvisionDevicesResponseV2
): Promise<void> {
  if (response.bodyId === null || response.bodyId === undefined) {
    return;
  }
  const previousBodyId: string = await DeviceModel.getBodyId();
  const partnerId: string =
    response.partnerId !== null && response.partnerId !== undefined
      ? response.partnerId
      : "";
  const partnerCustomerId: string =
    response.partnerCustomerId !== null &&
    response.partnerCustomerId !== undefined
      ? response.partnerCustomerId
      : "";
  await DeviceModel.storeProvisionDeviceData(
    response.bodyId,
    partnerId,
    partnerCustomerId
  );
  await TokenManager.rebindToCurrentBody(previousBodyId);
}
